import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type ShareHandler = (() => void) | null;

type ShareActionContextValue = {
  hasShareAction: boolean;
  openShare: () => void;
  setShareHandler: (handler: ShareHandler) => void;
};

const ShareActionContext = createContext<ShareActionContextValue | null>(null);

export const ShareActionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [handler, setHandler] = useState<ShareHandler>(null);

  const setShareHandler = useCallback((nextHandler: ShareHandler) => {
    setHandler(() => nextHandler);
  }, []);

  const openShare = useCallback(() => {
    if (handler) {
      handler();
    }
  }, [handler]);

  const value = useMemo(
    () => ({
      hasShareAction: Boolean(handler),
      openShare,
      setShareHandler,
    }),
    [handler, openShare, setShareHandler]
  );

  return (
    <ShareActionContext.Provider value={value}>
      {children}
    </ShareActionContext.Provider>
  );
};

export const useShareAction = (): ShareActionContextValue => {
  const context = useContext(ShareActionContext);
  if (!context) {
    throw new Error("useShareAction must be used within ShareActionProvider");
  }
  return context;
};
